import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiVideo, FiCheckCircle, FiAlertCircle, FiX, FiInfo } = FiIcons;

const isValidUrl = (str) => {
  if (!str) return false;
  try {
    const u = new URL(str);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
};

const VideoUrlInput = ({ value, onChange }) => {
  const [focused, setFocused] = useState(false);
  const isValid = !value || isValidUrl(value);
  const hasValue = value && value.trim().length > 0;

  const previewUrl = hasValue
    ? `${value.endsWith('/') ? value : value + '/'}ABCDEFGHIJKLMNOPQRSTUV.mp4`
    : null;

  return (
    <div className="p-4 bg-slate-950/60 rounded-lg border border-slate-800 space-y-3">
      <div className="flex items-center gap-2">
        <SafeIcon icon={FiVideo} className="text-indigo-400 text-sm" />
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Custom Video URL Path
        </span>
        <span className="ml-auto text-xs text-slate-600 italic">optional</span>
      </div>

      <div className="relative">
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="https://example.com/videos-url-goes-here/"
          className={`w-full bg-slate-950 border rounded-lg px-4 py-2.5 text-white text-sm placeholder-slate-600 outline-none transition-all pr-10 ${
            focused
              ? 'border-indigo-500 ring-2 ring-indigo-500/20'
              : hasValue
              ? isValid
                ? 'border-green-600/50'
                : 'border-red-600/50'
              : 'border-slate-700'
          }`}
        />
        {hasValue && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
            {isValid ? (
              <SafeIcon icon={FiCheckCircle} className="text-green-400 text-sm" />
            ) : (
              <SafeIcon icon={FiAlertCircle} className="text-red-400 text-sm" />
            )}
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-slate-600 hover:text-slate-400 transition-colors"
              title="Clear"
            >
              <SafeIcon icon={FiX} className="text-xs" />
            </button>
          </div>
        )}
      </div>

      {hasValue && !isValid && (
        <p className="text-xs text-red-400 flex items-center gap-1.5">
          <SafeIcon icon={FiAlertCircle} className="shrink-0" />
          Please enter a valid URL starting with http:// or https://
        </p>
      )}

      {hasValue && isValid && previewUrl && (
        <div className="p-2.5 bg-slate-900 rounded-lg border border-slate-700/50 space-y-1">
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
            Generated URL format:
          </p>
          <p className="text-xs font-mono text-purple-400 break-all">{previewUrl}</p>
          <p className="text-xs text-slate-600 italic">
            Each video gets a unique 22-char random alphanumeric filename
          </p>
        </div>
      )}

      <div className="flex items-start gap-2 p-2.5 bg-slate-800/40 rounded-lg">
        <SafeIcon icon={FiInfo} className="text-slate-500 text-xs shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500">
          Leave blank to use the default randomised video host. When set, all{' '}
          <strong className="text-slate-400">.mp4</strong> video URLs will use this base path
          with a random <strong className="text-slate-400">22-char alphanumeric</strong>{' '}
          filename — e.g.{' '}
          <code className="text-slate-400">base/ABCDEFGHIJKLMNOPQRSTUV.mp4</code>
        </p>
      </div>
    </div>
  );
};

export default VideoUrlInput;